import {
  RUN_HISTORY,
  SEVERITY_LABELS,
  type AuditRun,
  type Finding,
  type FindingStatus,
} from "./audit-data";
import { createDataTable, type Column } from "../ui/data-table";
import { createDrawer } from "../ui/drawer";
import { toFindings, type RunAuditResponse } from "./audit-api";
import type { RunSummaryDto, RunDetailDto } from "./history-api";
import { showToast } from "../ui/toast";

const STATUS_LABEL: Record<AuditRun["status"], string> = {
  completed: "完了",
  running: "実行中",
  failed: "失敗",
};

const TRIGGER_LABEL: Record<RunSummaryDto["trigger"], string> = {
  manual: "手動",
  scheduled: "スケジュール",
};

const FINDING_STATUS_LABEL: Record<FindingStatus, string> = {
  pass: "合格",
  fail: "要対応",
  na: "対象外",
  review: "要確認",
};

export interface HistoryDeps {
  // 実ラン履歴（接続済み時に注入）。省略時はサンプル(RUN_HISTORY)を表示。
  readonly runs?: readonly RunSummaryDto[];
  // 行クリック時にそのランの詳細(findings 込み)を取得する。runs と対で注入。
  readonly loadDetail?: (runId: string) => Promise<RunDetailDto>;
  // 接続を試みたが未接続/失敗でサンプルにフォールバックした場合 true。この時のみ接続誘導バナーを出す。
  readonly sampleMode?: boolean;
}

function fmtDateTime(iso: string): string {
  return iso.slice(0, 16).replace("T", " ");
}

function fmtCounts(c: RunSummaryDto["counts"]): string {
  return c.critical + "/" + c.high + "/" + c.medium + "/" + c.low;
}

function detailLoading(): HTMLElement {
  const p = document.createElement("p");
  p.dataset["testid"] = "history-detail-loading";
  p.className = "py-6 text-sm text-slate-500";
  p.textContent = "読み込み中...";
  return p;
}

function detailError(): HTMLElement {
  const p = document.createElement("p");
  p.dataset["testid"] = "history-detail-error";
  p.className = "py-6 text-sm text-rose-600";
  p.textContent = "詳細の取得に失敗しました。時間をおいて再度お試しください。";
  return p;
}

// ラン詳細(findings)を drawer の中身としてレンダリングする。
function buildDetailContent(d: RunDetailDto): HTMLElement {
  const resp: RunAuditResponse = {
    healthCheckScore: d.healthCheckScore,
    findings: d.findings,
    coverage: d.coverage,
    runAt: d.runAt,
    orgId: d.orgId,
    instanceUrl: "",
  };
  const { findings, notEvaluated } = toFindings(resp);

  const wrap = document.createElement("div");
  wrap.className = "space-y-3";

  const meta = document.createElement("p");
  meta.className = "text-sm text-slate-600";
  meta.textContent =
    "スコア " + d.healthCheckScore + "% ・ 検出(重大/高/中/低) " + fmtCounts(d.counts);
  wrap.append(meta);

  const columns: Column<Finding>[] = [
    { key: "severity", header: "重大度", cell: (f) => SEVERITY_LABELS[f.check.severity] },
    { key: "title", header: "チェック", cell: (f) => f.check.title },
    { key: "status", header: "状態", cell: (f) => FINDING_STATUS_LABEL[f.status] },
    { key: "affected", header: "影響数", cell: (f) => String(f.affectedCount) },
  ];
  wrap.append(
    createDataTable<Finding>({
      caption: "このランの検出一覧",
      columns,
      rows: findings,
      emptyMessage: "検出はありません。",
      rowTestId: "history-detail-row",
    }),
  );

  if (notEvaluated.length > 0) {
    const ne = document.createElement("p");
    ne.className = "text-sm text-slate-500";
    ne.dataset["testid"] = "history-detail-not-evaluated";
    ne.textContent = "未評価 " + notEvaluated.length + " 件（本診断の対象外）";
    wrap.append(ne);
  }
  return wrap;
}

// サンプル(モック)履歴を描画する。
function renderSample(root: HTMLElement, sampleMode: boolean): void {
  if (sampleMode) {
    const notice = document.createElement("p");
    notice.dataset["testid"] = "sample-notice";
    notice.className = "mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800";
    notice.textContent = "サンプルデータを表示中です。実データは「接続」タブで Salesforce と接続してください。";
    root.append(notice);
  }
  const columns: Column<AuditRun>[] = [
    { key: "ranAt", header: "日時", cell: (r) => fmtDateTime(r.ranAt) },
    { key: "score", header: "スコア", cell: (r) => r.score + "%" },
    { key: "counts", header: "検出(重大/高/中/低)", cell: (r) => fmtCounts(r.counts) },
    { key: "runBy", header: "実行者", cell: (r) => r.runBy },
    { key: "status", header: "ステータス", cell: (r) => STATUS_LABEL[r.status] },
  ];
  root.append(
    createDataTable<AuditRun>({ caption: "監査の実行履歴", columns, rows: RUN_HISTORY, rowTestId: "history-row" }),
  );
}

// 実ラン履歴を描画する。行クリックで loadDetail → drawer に findings を表示。
function renderRealRuns(
  root: HTMLElement,
  runs: readonly RunSummaryDto[],
  loadDetail: (runId: string) => Promise<RunDetailDto>,
): void {
  if (runs.length === 0) {
    const empty = document.createElement("p");
    empty.dataset["testid"] = "history-empty";
    empty.className = "py-6 text-sm text-slate-500";
    empty.textContent =
      "まだ診断履歴がありません。「設定チェックを実施する」で診断を実行してください。";
    root.append(empty);
    return;
  }

  const drawer = createDrawer({ title: "ラン詳細" });

  async function openDetail(run: RunSummaryDto): Promise<void> {
    drawer.setTitle(fmtDateTime(run.runAt) + " の診断結果");
    drawer.setContent(detailLoading());
    drawer.open();
    try {
      const detail = await loadDetail(run.runId);
      drawer.setContent(buildDetailContent(detail));
    } catch {
      drawer.setContent(detailError());
      showToast({ tone: "warning", title: "詳細の取得に失敗しました", message: "時間をおいて再度お試しください。" });
    }
  }

  const columns: Column<RunSummaryDto>[] = [
    { key: "runAt", header: "日時", cell: (r) => fmtDateTime(r.runAt) },
    { key: "score", header: "スコア", cell: (r) => r.healthCheckScore + "%" },
    { key: "counts", header: "検出(重大/高/中/低)", cell: (r) => fmtCounts(r.counts) },
    { key: "trigger", header: "種別", cell: (r) => TRIGGER_LABEL[r.trigger] },
    { key: "actorSub", header: "実行者", cell: (r) => r.actorSub },
  ];

  root.append(
    createDataTable<RunSummaryDto>({
      caption: "監査の実行履歴",
      columns,
      rows: runs,
      onRowClick: (run) => void openDetail(run),
      rowTestId: "history-row",
    }),
    drawer.element,
  );
}

export function renderHistory(root: HTMLElement, deps?: HistoryDeps): void {
  root.replaceChildren();

  // runs 未注入 = 純モック or 未接続 → サンプル表示（sampleMode 時のみ接続誘導バナー）。
  if (deps?.runs === undefined || deps.loadDetail === undefined) {
    renderSample(root, deps?.sampleMode === true);
    return;
  }

  renderRealRuns(root, deps.runs, deps.loadDetail);
}
